// The commit list and the branch graph (spec/dashboard.md section 12.1): the current branch's log with Load more by cursor,
// the first page of every other branch for the graph (a failing branch is left out), one lane per branch with the current
// first; "Current branch only" draws the current lane alone. A row is exactly ROW_HEIGHT high so it lines up with the SVG.
import { useEffect, useMemo, useState } from 'react';
import { ops } from '../../api/operations';
import type { Commit, Page } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { BranchGraph } from '../../history/BranchGraph';
import { buildGraph, type BranchLog } from '../../history/graph';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Switch } from '../../ui/inputs';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useQuery } from '../../workspace/cache';
import { PAGE_SIZE, ROW_HEIGHT, errorText, shortSha, subjectOf } from './util';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';
import { AlertCircle, GitCommitHorizontal } from '../../ui/icons';

const CURRENT_ONLY = new Set([0]);

export function CommitLog({ branch, selected, onSelect }: { branch: string; selected: string | null; onSelect: (sha: string) => void }) {
  const { client } = useAuth();
  const { org, game, branches, offsets } = useGame();
  const [zone] = useTimeZone();
  const [currentOnly, setCurrentOnly] = useState(false);
  const prefix = `branch:${game.id}:${branch}:history`;
  const list = (b: string, cursor?: string) =>
    client.data(ops.listCommits, { path: { org, game: game.slug, branch: b }, query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page<Commit>>;

  const first = useQuery(`${prefix}:log`, () => list(branch));
  const others = branches.filter((b) => b.name !== branch);
  const othersKey = others.map((b) => `${b.name}@${b.head ?? ''}`).join(',');
  const othersQ = useQuery(others.length > 0 ? `${prefix}:graph:${othersKey}` : null, async () => {
    const settled = await Promise.allSettled(others.map((b) => list(b.name)));
    return others.map((b, i): BranchLog | null => {
      const r = settled[i]!;
      return r.status === 'fulfilled' ? { branch: b.name, head: b.head, commits: r.value.items } : null;
    });
  });

  const [more, setMore] = useState<{ items: Commit[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  useEffect(() => {
    setMore(null);
    setMoreError(null);
  }, [first.data]);
  const cursor = more ? more.cursor : (first.data?.nextCursor ?? null);
  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await list(branch, cursor);
      setMore((m) => ({ items: [...(m?.items ?? []), ...page.items], cursor: page.nextCursor }));
    } catch (e) {
      setMoreError(errorText(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const current = branches.find((b) => b.name === branch);
  const graph = useMemo(() => {
    const own: BranchLog = { branch, head: current?.head ?? first.data?.items[0]?.sha ?? null, commits: [...(first.data?.items ?? []), ...(more?.items ?? [])] };
    if (currentOnly) return buildGraph([own]);
    const rest = (othersQ.data ?? []).filter((l): l is BranchLog => l !== null);
    return buildGraph([own, ...rest]);
  }, [branch, current?.head, first.data, more, othersQ.data, currentOnly]);
  const failed = (othersQ.data ?? []).map((l, i) => (l ? null : others[i]?.name)).filter((n): n is string => !!n);

  return (
    <div className="commit-log">
      <div className="page-toolbar commit-log-head">
        <span data-testid="current-branch-only">
          <Switch label="Current branch only" checked={currentOnly} onChange={setCurrentOnly} />
        </span>
        <span className="muted small">
          {graph.lanes.length} branch{graph.lanes.length === 1 ? '' : 'es'} drawn
          {failed.length > 0 ? ` · not drawn: ${failed.join(', ')}` : ''}
        </span>
      </div>
      {first.error ? (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">{errorText(first.error)}</div>
          <Button size="s" onClick={first.refetch}>
            Retry
          </Button>
        </div>
      ) : !first.data ? (
        <LoadingPane rows={6} label="Loading the history" />
      ) : graph.rows.length === 0 ? (
        <div data-testid="history-empty">
          <EmptyState icon={<GitCommitHorizontal />} title="No commit on this branch yet." body="Save a change in the branch workspace; every save is a commit here." />
        </div>
      ) : (
        <div className="pane commit-table">
          <BranchGraph rows={graph.rows} lanes={graph.lanes} rowHeight={ROW_HEIGHT} visibleLanes={currentOnly ? CURRENT_ONLY : undefined} selected={selected} onSelect={onSelect} />
          <ul className="commit-list" data-testid="commit-list" aria-label="Commits">
            {graph.rows.map((r) => (
              <li
                key={r.sha}
                data-testid={`commit-row-${r.sha}`}
                className={`commit-row${r.sha === selected ? ' selected' : ''}${r.lane === 0 ? '' : ' other-lane'}`}
                style={{ height: ROW_HEIGHT }}
                aria-current={r.sha === selected ? 'true' : undefined}
                onClick={() => onSelect(r.sha)}
              >
                <button type="button" className="linklike commit-sha" aria-label={`Open commit ${shortSha(r.sha)}`} title={r.sha}>
                  <code>{shortSha(r.sha)}</code>
                </button>
                <span className="commit-subject" title={r.commit.message}>
                  {subjectOf(r.commit.message)}
                </span>
                {r.merge && <Badge tone="info">merge</Badge>}
                {r.heads.map((h) => (
                  <Badge key={h} tone={h === branch ? 'accent' : 'neutral'} title={`The head of ${h}`}>
                    {h}
                  </Badge>
                ))}
                <span className="commit-author muted small">{r.commit.authorName}</span>
                <span className="commit-time muted small">{formatInstant(r.commit.authoredAt, zone, { offsets })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {cursor && (
        <div className="row commit-more">
          <Button size="s" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
      {moreError && (
        <LoadError>{moreError}</LoadError>
      )}
    </div>
  );
}
